import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  TextField,
  useTheme,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Stack,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, ViewColumn } from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { format } from 'date-fns';
import { tokens } from '../../../../theme';

import {
  useGetOrgMpDailyQuery,
  useGetOrgMpJobsQuery,
} from '../../../../features/organizations/organizationsApiSlice';

function TabPanel({ value, index, children, sx }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2, ...sx }}>{children}</Box>;
}

function ColumnsPopover({ anchorEl, onClose, columns, visibilityModel, setVisibilityModel, title }) {
  const open = Boolean(anchorEl);
  const id = open ? 'columns-popover' : undefined;

  const handleToggle = (field) => {
    setVisibilityModel((prev) => ({
      ...prev,
      [field]: !(prev?.[field] ?? true),
    }));
  };

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ sx: { p: 1.5, borderRadius: 2, minWidth: 260 } }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      <Divider sx={{ mb: 1 }} />
      <FormGroup>
        {columns.map((col) => (
          <FormControlLabel
            key={col.field}
            control={
              <Checkbox
                checked={visibilityModel?.[col.field] ?? true}
                onChange={() => handleToggle(col.field)}
                size="small"
              />
            }
            label={col.headerName}
          />
        ))}
      </FormGroup>
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button
          size="small"
          onClick={() =>
            setVisibilityModel(Object.fromEntries(columns.map((c) => [c.field, true])))
          }
        >
          Show all
        </Button>
        <Button
          size="small"
          onClick={() =>
            setVisibilityModel(Object.fromEntries(columns.map((c) => [c.field, false])))
          }
        >
          Hide all
        </Button>
      </Stack>
    </Popover>
  );
}

const MarketPulseTab = ({ organization, start, end, s3TargetBucket }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const orgId = organization?.organization_id;

  const tabTheme = useMemo(
    () =>
      createTheme({
        components: {
          MuiTabs: {
            styleOverrides: {
              indicator: {
                backgroundColor: colors.greenAccent[500],
                height: 3,
                borderRadius: 3,
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                minWidth: 0,
                paddingInline: 12,
                color: colors.grey[300],
                '&.Mui-selected': {
                  color: colors.greenAccent[500],
                  fontWeight: 600,
                },
                '&:hover': {
                  color: colors.greenAccent[300],
                  opacity: 0.9,
                },
              },
            },
          },
        },
      }),
    [colors]
  );

  // Sub-tabs + date for Jobs list
  const [mpTab, setMpTab] = useState(0); // 0 = Daily Summary, 1 = Jobs
  const [jobsDate, setJobsDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  // ===== Queries =====
  const {
    data: mpDailyData,
    isFetching: mpDailyLoading,
    refetch: refetchMpDaily,
  } = useGetOrgMpDailyQuery(orgId ? { id: orgId, start, end } : null, {
    skip: !orgId || !start || !end,
  });

  const {
    data: mpJobsData,
    isFetching: mpJobsLoading,
    refetch: refetchMpJobs,
  } = useGetOrgMpJobsQuery(orgId && jobsDate ? { id: orgId, date: jobsDate } : null, {
    skip: !orgId || !jobsDate,
  });

  // ===== Derived rows =====
  const mpDailyRows = (mpDailyData?.daily || []).map((d, i) => ({
    id: i + 1,
    date: d.date,
    calls_total: d.calls_total ?? 0,
    bulk_jobs: d.bulk_jobs ?? 0,
    topics_b2b: d.topics_b2b ?? 0,
    topics_b2c: d.topics_b2c ?? 0,
    topics_custom: d.topics_custom ?? 0,
    status_checks: d.status_checks ?? 0,
    single_topic: d.single_topic ?? 0,
  }));

  // Flatten jobs for richer columns
  const jobsRows = (mpJobsData?.jobs || []).map((j, i) => {
    const md = j.member_delivery || {};
    const ic = j.input_counts || {};
    const mt = j.matched_topics || {};
    const nf = j.not_found || {};
    const started = j.started || j.utc_job_processing_start || j.utc_job_submitted || '';
    const ended = j.ended || j.utc_job_processing_end || '';
    const bucket = j.bucket || (j.results?.files?.[0]?.bucket ?? '');
    const key = j.key || (j.results?.files?.[0]?.key ?? '');
    const resultUrl = j.result_presigned_url || j.results?.files?.[0]?.url || '';

    return {
      id: i + 1,
      process_id: j.process_id,
      state: j.state,
      started,
      ended,
      total_time: j.total_time ?? null,
      processing_time: j.processing_time ?? null,

      input_total: ic.total ?? null,
      input_b2b: ic.b2b ?? null,
      input_b2c: ic.b2c ?? null,
      input_custom: ic.custom ?? null,

      matched_rows: j.matched_rows ?? null,
      matched_b2b: mt.b2b ?? null,
      matched_b2c: mt.b2c ?? null,
      matched_custom: mt.custom ?? null,

      not_found_b2b: nf.b2b ?? null,
      not_found_b2c: nf.b2c ?? null,
      not_found_custom: nf.custom ?? null,

      member_delivery_enabled: md.enabled ?? null,
      member_delivery_requested: md.requested ?? null,
      member_delivery_copied: md.copied ?? null,
      member_delivery_copied_count: md.copied_count ?? null,

      bucket,
      key,
      result_link: resultUrl,
    };
  });

  // ===== Column defs (full sets) =====
  const mpDailyAllColumns = useMemo(
    () => [
      { field: 'date', headerName: 'Day', flex: 1, minWidth: 140 },
      { field: 'calls_total', headerName: 'Calls (All MP)', type: 'number', flex: 0.9, minWidth: 160 },
      { field: 'bulk_jobs', headerName: 'Bulk Jobs Submitted', type: 'number', flex: 1, minWidth: 180 },
      { field: 'topics_b2b', headerName: 'Topics (B2B)', type: 'number', flex: 0.9, minWidth: 140 },
      { field: 'topics_b2c', headerName: 'Topics (B2C)', type: 'number', flex: 0.9, minWidth: 140 },
      { field: 'topics_custom', headerName: 'Topics (Custom)', type: 'number', flex: 1, minWidth: 160 },
      { field: 'status_checks', headerName: 'Status Checks', type: 'number', flex: 0.9, minWidth: 140 },
      { field: 'single_topic', headerName: 'Single Topic Lookups', type: 'number', flex: 1.1, minWidth: 190 },
    ],
    []
  );

  const jobsAllColumns = useMemo(
    () => [
      { field: 'process_id', headerName: 'Process ID', flex: 1.4, minWidth: 220 },
      { field: 'state', headerName: 'Status', flex: 0.7, minWidth: 120 },
      { field: 'started', headerName: 'Started (UTC)', flex: 1, minWidth: 180 },
      { field: 'ended', headerName: 'Ended (UTC)', flex: 1, minWidth: 180 },
      { field: 'total_time', headerName: 'Total Time (s)', type: 'number', flex: 0.7, minWidth: 140 },
      { field: 'processing_time', headerName: 'Processing Time (s)', type: 'number', flex: 0.8, minWidth: 170 },

      { field: 'input_total', headerName: 'Inputs (Total)', type: 'number', flex: 0.7, minWidth: 140 },
      { field: 'input_b2b', headerName: 'Inputs (B2B)', type: 'number', flex: 0.7, minWidth: 130 },
      { field: 'input_b2c', headerName: 'Inputs (B2C)', type: 'number', flex: 0.7, minWidth: 130 },
      { field: 'input_custom', headerName: 'Inputs (Custom)', type: 'number', flex: 0.7, minWidth: 140 },

      { field: 'matched_rows', headerName: 'Matched Rows', type: 'number', flex: 0.8, minWidth: 150 },
      { field: 'matched_b2b', headerName: 'Matched Topics (B2B)', type: 'number', flex: 0.9, minWidth: 190 },
      { field: 'matched_b2c', headerName: 'Matched Topics (B2C)', type: 'number', flex: 0.9, minWidth: 190 },
      { field: 'matched_custom', headerName: 'Matched Topics (Custom)', type: 'number', flex: 1, minWidth: 210 },

      { field: 'not_found_b2b', headerName: 'Not Found (B2B)', type: 'number', flex: 0.9, minWidth: 160 },
      { field: 'not_found_b2c', headerName: 'Not Found (B2C)', type: 'number', flex: 0.9, minWidth: 160 },
      { field: 'not_found_custom', headerName: 'Not Found (Custom)', type: 'number', flex: 1, minWidth: 180 },

      { field: 'member_delivery_enabled', headerName: 'Member Delivery Enabled', type: 'boolean', flex: 0.9, minWidth: 210 },
      { field: 'member_delivery_requested', headerName: 'Member Delivery Requested', type: 'boolean', flex: 0.9, minWidth: 220 },
      { field: 'member_delivery_copied', headerName: 'Member Delivery Copied', type: 'boolean', flex: 0.9, minWidth: 200 },
      { field: 'member_delivery_copied_count', headerName: 'Copied File Count', type: 'number', flex: 0.9, minWidth: 170 },

      { field: 'bucket', headerName: 'Bucket', flex: 1.2, minWidth: 220 },
      { field: 'key', headerName: 'Key', flex: 1.6, minWidth: 280 },
      {
        field: 'result_link',
        headerName: 'Result Link',
        flex: 0.9,
        minWidth: 150,
        renderCell: (params) =>
          params.value ? (
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                window.open(params.value, '_blank', 'noopener,noreferrer');
              }}
            >
              Open
            </Button>
          ) : (
            '-'
          ),
      },
    ],
    []
  );

  // ===== Column Visibility with persistence =====
  const DAILY_KEY = 'mpDailyColsVisible';
  const JOBS_KEY = 'mpJobsColsVisible';

  const [dailyVisibility, setDailyVisibility] = useState({});
  const [jobsVisibility, setJobsVisibility] = useState({});

  // load persisted
  useEffect(() => {
    try {
      const d = JSON.parse(localStorage.getItem(DAILY_KEY) || 'null');
      const j = JSON.parse(localStorage.getItem(JOBS_KEY) || 'null');
      if (d && typeof d === 'object') setDailyVisibility(d);
      if (j && typeof j === 'object') setJobsVisibility(j);
    } catch {
      // ignore
    }
  }, []);

  // persist on change
  useEffect(() => {
    try {
      localStorage.setItem(DAILY_KEY, JSON.stringify(dailyVisibility));
    } catch {}
  }, [dailyVisibility]);
  useEffect(() => {
    try {
      localStorage.setItem(JOBS_KEY, JSON.stringify(jobsVisibility));
    } catch {}
  }, [jobsVisibility]);

  // columns popover state
  const [dailyColsAnchor, setDailyColsAnchor] = useState(null);
  const [jobsColsAnchor, setJobsColsAnchor] = useState(null);

  const mpPath = s3TargetBucket ? `s3://${s3TargetBucket}/outgoing/apis/market_pulse/` : null;

  return (
    <Box>
      {/* Product-specific output path hint */}
      {mpPath && (
        <Paper variant="outlined" sx={{ p: 1.25, mb: 2, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Market Pulse output path (when S3 delivery is enabled):{' '}
            <span style={{ fontFamily: 'monospace' }}>{mpPath}</span>
          </Typography>
        </Paper>
      )}

      <ThemeProvider theme={tabTheme}>
        <Tabs value={mpTab} onChange={(_, v) => setMpTab(v)} sx={{ mb: 2 }}>
          <Tab label="Daily Summary" />
          <Tab label="Jobs" />
        </Tabs>
      </ThemeProvider>

      {/* MP sub-tab 0: Daily Summary */}
      <TabPanel value={mpTab} index={0} sx={{ pt: 0 }}>
        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Grid item>
            <Typography variant="h6">Market Pulse — Daily Summary</Typography>
            <Typography variant="body2" color="text.secondary">
              Range: <strong>{start}</strong> → <strong>{end}</strong>
            </Typography>
          </Grid>
          <Grid item>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                startIcon={<ViewColumn />}
                onClick={(e) => setDailyColsAnchor(e.currentTarget)}
              >
                Columns
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => refetchMpDaily()}
                disabled={mpDailyLoading}
              >
                Refresh
              </Button>
            </Stack>
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ borderRadius: 2, height: 420 }}>
          <DataGrid
            rows={mpDailyRows}
            columns={mpDailyAllColumns}
            columnVisibilityModel={dailyVisibility}
            onColumnVisibilityModelChange={setDailyVisibility}
            disableRowSelectionOnClick
            loading={mpDailyLoading}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'date', sort: 'asc' }] },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </Paper>
        {!mpDailyLoading && mpDailyRows.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No Market Pulse usage in this range.
          </Typography>
        )}

        <ColumnsPopover
          anchorEl={dailyColsAnchor}
          onClose={() => setDailyColsAnchor(null)}
          columns={mpDailyAllColumns}
          visibilityModel={dailyVisibility}
          setVisibilityModel={setDailyVisibility}
          title="Daily Summary Columns"
        />
      </TabPanel>

      {/* MP sub-tab 1: Jobs (per-day) */}
      <TabPanel value={mpTab} index={1} sx={{ pt: 0 }}>
        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Grid item>
            <Typography variant="h6">Jobs — {jobsDate}</Typography>
            <Typography variant="body2" color="text.secondary">
              Showing jobs submitted on this date (from job status files).
            </Typography>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                type="date"
                label="Day"
                value={jobsDate}
                onChange={(e) => setJobsDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<ViewColumn />}
                onClick={(e) => setJobsColsAnchor(e.currentTarget)}
              >
                Columns
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => refetchMpJobs()}
                disabled={mpJobsLoading}
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ borderRadius: 2, height: 420 }}>
          <DataGrid
            rows={jobsRows}
            columns={jobsAllColumns}
            columnVisibilityModel={jobsVisibility}
            onColumnVisibilityModelChange={setJobsVisibility}
            disableRowSelectionOnClick
            loading={mpJobsLoading}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'started', sort: 'asc' }] },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </Paper>
        {!mpJobsLoading && jobsRows.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No jobs found for this day.
          </Typography>
        )}

        <ColumnsPopover
          anchorEl={jobsColsAnchor}
          onClose={() => setJobsColsAnchor(null)}
          columns={jobsAllColumns}
          visibilityModel={jobsVisibility}
          setVisibilityModel={setJobsVisibility}
          title="Jobs Columns"
        />
      </TabPanel>
    </Box>
  );
};

export default MarketPulseTab;
